/**
 * Application entry point.
 *
 * This file includes `import`'s for all the components/directives used
 * by the HTML page and the controller to provide the configuration.
 */

import './sass/desktop.scss';
import 'gmf/controllers/desktop.scss';
import './sass/vars_desktop.scss';

import angular from 'angular';
import gmfControllersAbstractDesktopController, {AbstractDesktopController}
  from 'gmf/controllers/AbstractDesktopController.js';
import ngeoGeolocationDesktop from 'ngeo/geolocation/desktop.js';
import rudazModule from '../rudazmodule.js';
import EPSG2056 from '@geoblocks/proj/src/EPSG_2056.js';
import EPSG21781 from '@geoblocks/proj/src/EPSG_21781.js';
import Raven from 'raven-js/src/raven.js';
import RavenPluginsAngular from 'raven-js/plugins/angular.js';
import rudazInfra3DModule from 'rudaz/infra3d/infra3dComponent.js';
import rudazOrbis360Module from 'rudaz/orbis360/orbis360Component.js';
import rudazFireSimulatorModule from 'rudaz/firesimulator/fireSimulatorComponent.js';
import gmfLidarprofileModule from 'gmf/lidarprofile/module.js';
import ngeoGooglestreetviewModule from 'ngeo/googlestreetview/module.js';
import olStyleCircle from 'ol/style/Circle.js';
import olStyleFill from 'ol/style/Fill.js';
import olStyleStroke from 'ol/style/Stroke.js';
import olStyleStyle from 'ol/style/Style.js';
import olStyleRegularShape from 'ol/style/RegularShape';
import ngeoMessagePopupComponent from 'ngeo/message/popupComponent.js';
import ngeoMessagePopup from 'ngeo/message/Popup.js';

if (!window.requestAnimationFrame) {
  alert('Your browser is not supported, please update it or use another one. You will be redirected.\n\n'
    + 'Votre navigateur n\'est pas supporté, veuillez le mettre à jour ou en utiliser un autre. '
    + 'Vous allez être redirigé.\n\n'
    + 'Ihr Browser wird nicht unterstützt, bitte aktualisieren Sie ihn oder verwenden Sie einen anderen. '
    + 'Sie werden weitergeleitet.');
  window.location.href = 'https://geomapfish.org/';
}


/**
 * @private
 */
class Controller extends AbstractDesktopController {
  /**
   * @param {angular.IScope} $scope Scope.
   * @param {angular.auto.IInjectorService} $injector Main injector.
   * @param {!import('ngeo/query/MapQuerent.js').QueryResult} ngeoQueryResult ngeo query result.
   */
  constructor($scope, $injector, ngeoQueryResult, ngeoCreatePopup) {
    super({
      srid: 2056,
      mapViewConfig: {
        center: JSON.parse($injector.get('initialCenter')),
        zoom: JSON.parse($injector.get('initialZoom')),
        resolutions: [250, 100, 50, 20, 10, 5, 2, 1, 0.5, 0.25, 0.1, 0.05, 0.025, 0.0125]
      }
    }, $scope, $injector);

    this.ngeoQueryResult = ngeoQueryResult;

    this.$scope_ = $scope;
    // importer ngeoCreatePopup
    this.iframePopup = function(title, url, width, height) {
      const popup = ngeoCreatePopup();
      popup.setAutoDestroy(true);
      popup.addClass('popup-with-iframe');
      popup.setTitle(title);
      popup.setUrl(url);
      popup.setSize(width+'px', height+'px');
      popup.setOpen(true);
    };

    // Read object informations using window.speechSynthesis
    var playLang = this.lang === 'de' ? "de-CH" : "fr-CH" ;
    this.playInfosGCSPDS = function(data) {
        if(window.speechSynthesis.speaking) {
            window.speechSynthesis.cancel();
        } else {
            var msg = new SpeechSynthesisUtterance();
            msg.lang = playLang;
            msg.rate = 1; // Read speed
            var clearDataTag = document.getElementsByClassName("animation-container-detailed")[0]; // Get content from dom
            var audioDiv = document.createElement('div'); // Create new div for audio
            audioDiv.innerHTML = clearDataTag.innerHTML;
            var table = audioDiv.querySelector("tbody");
            var firstTr = table.querySelector("tr:nth-child(1)");
            var secondTr = table.querySelector("tr:nth-child(2)");
            table.removeChild(firstTr);
            table.removeChild(secondTr);
            var clearDataText = audioDiv.innerText || audioDiv.textContent;
            msg.text = clearDataText;
            window.speechSynthesis.speak(msg);
        }
    };

    // Open popup to print object attribute values
    this.printInfosGCSPDS = function(data) {
        var dataContent = document.getElementsByClassName("animation-container-detailed")[0]; // Get content from dom
        var printDiv = document.createElement('div'); // Create new div for print
        printDiv.innerHTML = dataContent.innerHTML;
        var table = printDiv.querySelector("tbody");
        var firstTr = table.querySelector("tr:nth-child(1)");
        var secondTr = table.querySelector("tr:nth-child(2)");
        table.removeChild(firstTr);
        table.removeChild(secondTr);
        var dataContentPrint = printDiv.innerHTML;
        var printWindow = window.open('', '', 'height=500, width=500');
        printWindow.document.write('<html><head><style>td {border-bottom: 1px solid #D6D6D6;} body {font-family: Arial, Helvetica, sans-serif; margin:20px;} .title {font-size: 1.5em;font-weight: bold;}</style></head><body >');
        printWindow.document.write(dataContentPrint);
        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.print();
    }

    this.imgPopup = function(imgUrl) {
        const popup = ngeoCreatePopup();
        popup.setAutoDestroy(true);
        popup.addClass('popup-with-iframe');
        popup.setContent('<img width="100%" src="'+imgUrl+'"/>');
        popup.setSize('100%', '100%');
        popup.setOpen(true);
    };

    // Bindings to rudazExterns
    window['rudazExterns'] = [];
    window['rudazExterns']['openPopup'] = this.iframePopup;
    window['rudazExterns']['openPopupImg'] = this.imgPopup;
    window['rudazExterns']['playInfos'] = this.playInfosGCSPDS;
    window['rudazExterns']['printInfos'] = this.printInfosGCSPDS;

    /**
     * @type {number}
     * @export
     */
    this.searchDelay = 50;

    /**
     * @type {Array<string>}
     */
    this.searchCoordinatesProjections = [EPSG21781, EPSG2056, 'EPSG:4326'];

    /**
     * @type {!Array<number>}
     */
    this.scaleSelectorValues = [250000, 100000, 50000, 20000, 10000, 5000, 2000, 1000, 500, 250, 100, 50, 25, 12.5];

    /**
     * @type {Array<string>}
     */
    this.elevationLayers = ['mnt_vsgis'];

    /**
     * @type {Object<string, import('gmf/mobile/measure/pointComponent.js').LayerConfig>}
     */
    this.elevationLayersConfig = {};

    /**
     * @type {string}
     */
    this.selectedElevationLayer = this.elevationLayers[0];

    // Geolocate me
    const positionFeatureStyle = new olStyleStyle({
      image: new olStyleCircle({
        radius: 6,
        fill: new olStyleFill({color: 'rgba(231, 1, 1, 1)'}),
        stroke: new olStyleStroke({color: 'rgba(255, 255, 255, 1)', width: 2})
      })
    });

    const accuracyFeatureStyle = accuracyFeatureStyle || new olStyleStyle({
      fill: new olStyleFill({color: 'rgba(220, 34, 40, 0.1)'}),
      stroke: new olStyleStroke({color: 'rgba(220, 34, 40, 1)', width: 2})
    });

    /**
     * @type {import('ngeo/geolocation/desktop.js').desktopGeolocationDirectiveOptions}
     */
    this.desktopGeolocationOptions = {
      positionFeatureStyle: positionFeatureStyle,
      accuracyFeatureStyle: accuracyFeatureStyle,
      zoom: undefined,
      autorotate: false
    };
    
    this.filterSelectorActive = false;
    this.filterSelectorEnabled = true;

    this.googleStreetViewActive = false;

    /**
     * @type {Object<string, import('gmf/profile/component.js').ProfileLineConfiguration>}
     */
    this.profileLinesconfiguration = {
      'mnt_vsgis': {color: '#DC2228'},
    };
    
    // Add specific mnt per instance
    if($injector.get('instance') == 'bitsch') {
      this.elevationLayers.push('mnt_bitsch');
      this.profileLinesconfiguration['mnt_bitsch'] = {color: '#0000FF'};
    }

    /**
     * @type {Array<import('gmf/map/mousepositionComponent.js').MousePositionProjection>}
     */
    this.mousePositionProjections = [{
      code: EPSG2056,
      label: 'CH1903+ / LV95',
      filter: 'ngeoNumberCoordinates::{x}, {y} m'
    }, {
      code: EPSG21781,
      label: 'CH1903 / LV03',
      filter: 'ngeoNumberCoordinates::{x}, {y} m'
    }, {
      code: 'EPSG:4326',
      label: 'WGS84',
      filter: 'ngeoDMSCoordinates:2'
    }];

    /**
     * @type {GridMergeTabs}
     */
    this.gridMergeTabs = {};

    // Allow angular-gettext-tools to collect the strings to translate
    /** @type {angular.gettext.gettextCatalog} */
    const gettextCatalog = $injector.get('gettextCatalog');
    gettextCatalog.getString('tsearch_add_theme');
    gettextCatalog.getString('tsearch_add_sub_theme');
    gettextCatalog.getString('tsearch_add_layer');

    /**
     * @type {boolean}
     * @export
     */
    this.infra3DActive = false;

    /**
     * @type {string}
     * @export
     */
    this.infra3DUrl = 'https://client-v3.infra3d.ch/';
    this.showInfra3D = $injector.get('showInfra3D').toLowerCase() == 'true' ? true : false ;

    this.showLidarProfile = $injector.get('showLidarProfile').toLowerCase() == 'true' ? true : false ;

    this.orbis360Active = false;
    this.orbis360Url = this.ngeoLocation.schema_ + "://" + this.ngeoLocation.domain_ + '/orbis360/index.html';
    this.showOrbis360 = $injector.get('showOrbis360').toLowerCase() == 'true' ? true : false ;
    this.orbis360Username = null;
    this.orbis360Password = null;
    this.orbis360Publication = null;
    this.orbis360PublicationId = null;

    this.showFireSimulator = false;
    this.fireSimulatorUrl = this.ngeoLocation.schema_ + "://" + this.ngeoLocation.domain_ + '/firesimulator/index.html';
    this.fireSimulatorActive = false;

    /**
     * @type {string}
     * @export
     */
    this.instanceName = $injector.get('instance');

    /* Pompier Valais central, display windows popup*/
    if(this.instanceName == "gcspds" || this.instanceName == "cspsalentin" || this.instanceName == "csiprintse" || this.instanceName == "scvs" || this.instanceName == "csimartigny" || this.instanceName == "cspsaviese" || this.instanceName == "csision") {
        this.displaywindowOpen = true;
        if(this.lang === 'de') {
            this.displaywindowTitle = "Schweigepflicht";
            this.displaywindowContent = "<div class='home-popup'><p>Wir weisen Sie darauf hin, dass die Informationen auf VSGIS.ch streng vertraulich sind. Es ist Ihnen daher untersagt, diese Informationen weiterzugeben. Sie sind nämlich an die Schweigepflicht gebunden, insbesondere gemäss dem Bundesgesetz über den Datenschutz (DSG). Eine Verletzung der Schweigepflicht ist rechtlich strafbar.</p> <p>Im Folgenden geben wir Ihnen den entsprechenden Auszug aus der Rechtsgrundlage bekannt. Die Pflicht zur Geheimhaltung bleibt auch nach dem Verlassen des Brandbekämpfungszentrums bestehen.</p> <h4>Bundesgesetz über den Datenschutz (DSG) vom 25. September 2020</h4> <p>Art. 62 Verletzung der Schweigepflicht<br/><br>1 Mit Busse bis zu 250'000 Franken wird, auf Antrag, bestraft, wer vorsätzlich geheime Personendaten, von denen er in Ausübung eines Berufes, der die Kenntnis solcher Daten erfordert, Kenntnis erlangt hat, offenbart.<br>2 Mit der gleichen Strafe wird bestraft, wer vorsätzlich geheime Personendaten offenbart, von denen er im Rahmen seiner Tätigkeit für eine schweigepflichtige Person oder während seiner Ausbildung bei dieser Person Kenntnis erlangt hat.<br>3 Die Offenbarung geheimer Personendaten bleibt auch dann strafbar, wenn die Berufsausübung oder die Ausbildung beendet ist.</p> <h4>Sicherheitsregeln</h4> <p><strong>Benutzername</strong><br/>Sie müssen sie sicher aufbewahren und dürfen sie nur auf begründete Anfrage an befugte Personen weitergeben.</p> <p><strong>Passwort</strong><br/>Sie ist nur Ihnen bekannt und Sie dürfen sie niemals an andere Personen weitergeben, auch nicht an Mitarbeiter der Supportzentren (Helpdesk).</p> Durch Schließen dieses Fensters bestätigen Sie, dass Sie die Nutzungsbedingungen zur Kenntnis genommen haben und ihnen zustimmen.</div>";
        } else {
            this.displaywindowTitle = "Devoir de discrétion";
            this.displaywindowContent = "<div class='home-popup'><p>Nous vous rendons attentifs(ves) au fait que les informations contenues sur le « VSGIS.ch » sont strictement confidentielles. Il est par conséquent interdit de les divulguer. Vous êtes en effet lié(e) par le devoir de discrétion, notamment selon la loi fédérale sur la protection des données (LPD). Une violation du devoir de discrétion est légalement répréhensible.</p> <p>Ci-après, nous vous donnons connaissance de l’extrait de base légale s’y rapportant. L’obligation de garder le secret demeure même après avoir quitté le Centre de Secours Incendie.</p> <h4>Loi fédérale sur la protection des données (LPD) du 25 septembre 2020</h4> <p>Art. 62 Violation du devoir de discrétion<br>1 Est, sur plainte, puni d’une amende de 250'000 francs au plus quiconque révèle intentionnellement des données personnelles secrètes portées à sa connaissance dans l’exercice d’une profession qui requiert la connaissance de telles données.<br>2 Est passible de la même peine quiconque révèle intentionnellement des données personnelles secrètes portées à sa connaissance dans le cadre des activités qu’il exerce pour le compte d’une personne soumise à l’obligation de garder le secret ou lors de sa formation chez elle.<br>3 La révélation de données personnelles secrètes demeure punissable alors même que l’exercice de la profession ou la formation ont pris fin.</p> <h4>Règles de sécurité</h4> <p><strong>Identifiant</strong><br/>Vous devez le conserver en sûreté et ne le communiquer qu’aux personnes autorisées sur demande justifiée.</p> <p><strong>Mot de passe</strong><br/>Il n’est connu que de vous et vous ne devez jamais le communiquer à qui que ce soit, même au personnel des centres de support (helpdesk).</p> En fermant cette fenêtre, vous confirmez avoir pris connaissance et acceptez les modalités d'utilisation.</div>";
        }
    }
    /**
     * @type {undefined|string}
     * @private
     */
    this.parznum_ = undefined;

    // Query style custom geom
    // Element selected on map
    const hlFill = new olStyleFill({color: [220, 34, 40, 0.2]});
    const hlStroke = new olStyleStroke({color: [220, 34, 40, 0.7], width: 2});
    this.queryFeatureStyle = new olStyleStyle({
      fill: hlFill,
      image: new olStyleCircle({
        fill: hlFill,
        radius: 5,
        stroke: hlStroke
      }),
      stroke: hlStroke
    });
    
    // Element selected in the grid
    const selectedFill = new olStyleFill({color: [220, 34, 40, 0.5]});
    const selectedStroke = new olStyleStroke({color: [255, 0, 0, 1], width: 2});
    this.selectedQueryFeatureStyle = new olStyleStyle({
      fill: selectedFill,
      image: new olStyleCircle({fill: selectedFill, radius: 5, stroke: selectedStroke}),
      stroke: selectedStroke
    });
    
    // Search highlight style
    const searchFill = new olStyleFill({color: [252, 148, 0, 0.6]});
    const searchStroke = new olStyleStroke({color: [252, 148, 0, 1], width: 2});
    const defaultSearchStyle = new olStyleStyle({
      fill: searchFill,
      image: new olStyleCircle({fill: searchFill, radius: 5, stroke: searchStroke}),
      stroke: searchStroke
    });

    /**
     * @type {Object<string, import('ol/style/Style.js').default>} Map of styles for search overlay.
     * @export
     */
    this.searchStyles = {
      'default': defaultSearchStyle,
      'gmfCoordinatesLayerName': new olStyleStyle({
        image: new olStyleRegularShape({
          stroke: new olStyleStroke({color: [220, 34, 40, 0.9], width: 2}),
          points: 4,
          radius: 8,
          radius2: 0,
          angle: 0
        })
      })
    };

    /**
     * @type {string}
     * @export
     */
    this.mobileUrl = this.ngeoLocation.schema_ + "://" + this.ngeoLocation.domain_ + this.ngeoLocation.path_ + "/mobile";
    this.show3dIcon = false;
    this.map3dUrl = "#";
    this.listInstanceNonCommunale = ["a9", "aluinfra", "aletsch", "aletschfeuerwehr", "aluinfra", "bubo", "csimartigny", "csiprintse", "csision", "cspsalentin", "cspsaviese", "gampelstegfeuerwehr", "gcspds", "gkw", "jodernkellerei", "lonza", "luetzelflueh", "nvrmsa", "rfsgoms", "rfsvisp", "saastalfeuerwehr", "scvs", "simplonnord", "sse", "steffisburg", "untergomsfeuerwehr", "vispfeuerwehr", "", "", ""];
    if(!this.listInstanceNonCommunale.includes(this.instanceName)) {
        this.show3dIcon = true;
        this.map3dUrl ="https://map3d.vsgis.ch/" + this.ngeoLocation.path_.substring(1);
    }
    this.orderDataUrl = "https://www.vsgis.ch/order";
    this.displayquerywindow = false;
    if(this.instanceName == "gcspds" || this.instanceName == "cspsalentin" || this.instanceName == "csiprintse" || this.instanceName == "csision" || this.instanceName == "scvs" || this.instanceName == "csimartigny" || this.instanceName == "cspsaviese") {
        this.displayquerywindow = true;
    }

    // Watch user status
    this.$scope_.$watchCollection(
      () => this.gmfUser, (newUser, oldUser) => {
        // Hide show infra3D button if user is logged in AND instance config has showInfra3D to true
        this.showInfra3D = $injector.get('showInfra3D').toLowerCase() == 'true' && newUser.username !== null ? true : false ;
        this.showOrbis360 = $injector.get('showOrbis360').toLowerCase() == 'true' && newUser.username !== null ? true : false ;
        if(this.instanceName == "gcspds" || this.instanceName == "cspsaviese") { // Currently only for gcspds  || this.instanceName == "cspsalentin" || this.instanceName == "csiprintse" || this.instanceName == "scvs" || this.instanceName == "csimartigny"
            this.showFireSimulator = newUser.username !== null ? true : false;
        }
        this.showLidarProfile = $injector.get('showLidarProfile').toLowerCase() == 'true' && newUser.username !== null ? true : false ;
        this.orbis360Username = newUser.username !== null ? $injector.get('orbis360Username') : null ;
        this.orbis360Password = newUser.username !== null ? $injector.get('orbis360Password') : null ;
        this.orbis360Publication = newUser.username !== null ? $injector.get('orbis360Publication') : null ;
        this.orbis360PublicationId = newUser.username !== null ? $injector.get('orbis360PublicationId') : null ;
        // Filter selector only for logged user
        this.filterSelectorEnabled = newUser.username !== null ? true : false;
        this.showHideDownloadLink();
        if(newUser.username !== null) { // User logged in
          this.showGoogleStreetView = ["brigglis", "leuk", "naters", "sierre", "visp"].includes(this.instanceName) ? true : false; // Activate googleStreetViewModule only for specified instances
        } else { // User logged off
          this.showGoogleStreetView = false;
        }
      }
    )
    // Watch editFeatureVectorLayer
    this.$scope_.$watchCollection(
      () => this.editFeatureVectorLayer, (newEF, oldEF) => {
        // Call settimeout to wait dom to be drawn
        setTimeout(function() {
          var editFeatureSelector = document.getElementsByTagName("gmf-editfeature");
          if(editFeatureSelector.length > 0) {
            // Bubo specific - order by partner dropdown list
            var selectElements = editFeatureSelector[0].getElementsByTagName("select");
            if(selectElements.cepage !== undefined && selectElements.cepage.options !== undefined) {
                var options = selectElements.cepage.options;
                var optionsArray = [];
                for (var i = 0; i < options.length; i++) {
                    optionsArray.push(options[i]);
                }
                optionsArray.sort((a, b) => a.text.trim().toLowerCase().localeCompare(b.text.trim().toLowerCase()));
                for (var i = 0; i <= options.length; i++) {
                    options[i] = optionsArray[i];
                }
            }
            if(selectElements.partner !== undefined && selectElements.partner.options !== undefined) {
                var options = selectElements.partner.options;
                var optionsArray = [];
                for (var i = 0; i < options.length; i++) {
                    optionsArray.push(options[i]);
                }
                optionsArray.sort((a, b) => a.text.trim().toLowerCase().localeCompare(b.text.trim().toLowerCase()));
                for (var i = 0; i <= options.length; i++) {
                    options[i] = optionsArray[i];
                }
            }

            // file manager - a href on link + Bubo specific datepicker = NOW
            var domElementsArray = Array.from(editFeatureSelector[0].getElementsByClassName("form-control"));
            domElementsArray.forEach(function (domElement) {
              // Bubo current date button
              if(domElement.getAttribute("name") == "last_treatment_date" && domElement.value == '') {
                  var currentDate = new Date();
                  var month = currentDate.getMonth()+1 < 10 ? '0'+(currentDate.getMonth()+1) : currentDate.getMonth()+1;
                  var day = currentDate.getDate() < 10 ? '0'+currentDate.getDate() : currentDate.getDate();
                  var hour = currentDate.getHours() < 10 ? '0'+currentDate.getHours() : currentDate.getHours();
                  var minutes = currentDate.getMinutes() < 10 ? '0'+currentDate.getMinutes() : currentDate.getMinutes();
                  domElement.nextElementSibling.remove();
                  var currentDateLink = document.createElement('a');
                  var currentDateText = document.createTextNode('Heure actuelle');
                  currentDateLink.appendChild(currentDateText);
                  currentDateLink.href = "#";
                  currentDateLink.className = "btn btn-secondary";
                  currentDateLink.onclick=function(){
                      domElement.focus();
                      domElement.value = currentDate.getFullYear() + '-' + month + '-' + day + ' ' + hour + ':' + minutes;
                      domElement.blur();
                      Array.from(document.getElementsByClassName('xdsoft_datetimepicker')).forEach(function(calendar) {
                        calendar.style.visibility = "hidden";
                      });
                      document.getElementsByName('notice')[0].focus();
                  };
                  domElement.after(currentDateLink);
              }
			  // file manager link
              if(domElement.innerText.trim().startsWith("http")) {
                domElement.style.color = "#DC2228";
                domElement.style.cursor = "pointer";
                domElement.addEventListener("click", function() {
                  window.open(domElement.innerText.trim(), '_blank');
                })
              }
            });
          }
      }, 500);
    });
    this.exportPdfUrl = null;
    this.exportXlsUrl = null;
    // Watch the ngeo query result service.
    this.$scope_.$watchCollection(
      () => ngeoQueryResult,
      (newQueryResult, oldQueryResult) => {
        if (newQueryResult !== oldQueryResult) {
          if (ngeoQueryResult.total > 0 && ngeoQueryResult.pending === false) {
            var listParcelle = [];
            var listRegion = [];
            ngeoQueryResult.sources.forEach(source => {
              // Parzellen
              if(source.label == 'av_ls_parzellen_ohne_eig_oeff' || source.label == 'av_ls_selbstrecht_ohne_eig_oeff') {
                source.features.forEach(feature => {
                  if(feature.get('parz_nummer') && feature.get('region')) {
                    listParcelle.push(feature.get('parz_nummer'));
                    listRegion.push(feature.get('region'));
                  }
                });
              }
            });
            if(listParcelle.length > 0 && listRegion.length > 0) {
              // Array.toString() transform array to csv
              var schemaHash = btoa(btoa(this.instanceName + '_geodata'));
              var listParcelleHash = btoa(btoa(listParcelle.toString()));
              var listRegionHash = btoa(btoa(listRegion.toString()));
              this.exportPdfUrl = this.ngeoLocation.schema_ + "://" + this.ngeoLocation.domain_ + `/vsgis-export-eig/reppdf.php?schema=${schemaHash}&parznum=${listParcelleHash}&region=${listRegionHash}&lang=${this.lang}`;
              this.exportXlsUrl = this.ngeoLocation.schema_ + "://" + this.ngeoLocation.domain_ + `/vsgis-export-eig/repxls.php?schema=${schemaHash}&parznum=${listParcelleHash}&region=${listRegionHash}&lang=${this.lang}`;
                }
          } else {
            this.exportPdfUrl = null;
            this.exportXlsUrl = null;
          }
            this.showHideDownloadLink();
        }
      });

    // Custom button
    this.downloadActive = false;
    this.helpActive = false;
    this.helpFileUrl = this.lang === 'de' ? 'https://docs.vsgis.ch/help/0933a032.pdf' : 'https://docs.vsgis.ch/help/0933a032_F.pdf';
    this.helpdeskUrl = this.lan === 'de' ? 'https://helpdesk.vsgis.ch/?lang=de' : 'https://helpdesk.vsgis.ch/';

    if ($injector.has('sentryUrl')) {
      const options = $injector.has('sentryOptions') ? $injector.get('sentryOptions') : undefined;
      const raven = new Raven();
      raven.config($injector.get('sentryUrl'), options)
        .addPlugin(RavenPluginsAngular)
        .install();
    }
    this.showHideDownloadLink = function() {
      this.downloadButtons = [];
      if(this.gmfUser.username !== null && this.gmfUser.roles.filter(r => r.name === 'role_parcelle_vignes_proprio').length == 0) { // User logged in and not has role role_parcelle_vignes_proprio (cransmontana)
        if(["lonza", "gkw"].includes(this.instanceName)) {
          //this.downloadButtons.find(obj => obj.id == 'download_eig_pdf').visible = false;
          //this.downloadButtons.find(obj => obj.id == 'download_eig_xls').visible = false;
        } else {
          var exportPdfXls = [];
          if(this.exportPdfUrl != null) {
            exportPdfXls.push({id:'download_eig_pdf', link: this.exportPdfUrl, visible: true, label: 'desktop_download_eig_pdf', zIndex: 1});
 
          }
          if(this.exportXlsUrl != null) {
            exportPdfXls.push({id:'download_eig_xls', link: this.exportXlsUrl, visible: true, label: 'desktop_download_eig_xls', zIndex: 2});
          }
          if(exportPdfXls.length > 0 ) {
            this.downloadButtons.push({id:"downloadpdfxls", label:"desktop_download", links:exportPdfXls});
          }
        }
      }

      // Documents button
      if(this.instanceName == "grimisuat") {
        var grimisuat = [];
        grimisuat.push({id:'documents', link: "https://docs.vsgis.ch/grimisuat/DIVERS/6263_docs.html", visible: true, label: 'desktop_download_documents', zIndex: 100});
        this.downloadButtons.push({id:"downloadgrimisuat", label:"desktop_download", links:grimisuat});
      }
      if(this.instanceName == "chalais" && this.gmfUser.username !== null) {
        var chalais = [];
        chalais.push({id:'documents', link: "https://docs.vsgis.ch/chalais/DIVERS/6232_docs.html", visible: true, label: 'desktop_download_documents', zIndex: 100})
        this.downloadButtons.push({id:"downloadchalais", label:"desktop_download", links:chalais});
      }
      // Orsieres specific button
      if(this.instanceName == "orsieres" && this.gmfUser.username !== null) {
        var orsieres = [];
        orsieres.push({id:'cad_dwg', link: 'https://docs.vsgis.ch/orsieres/ExportDWG/CAD_Orsieres.dwg', visible: true, label: 'desktop_download_orsieres_cad_dwg', zIndex: 11});
        orsieres.push({id:'ep_dwg', link: 'https://docs.vsgis.ch/orsieres/ExportDWG/EP_Orsieres.dwg', visible: true, label: 'desktop_download_orsieres_ep_dwg', zIndex: 12});
        orsieres.push({id:'eu_dwg', link: 'https://docs.vsgis.ch/orsieres/ExportDWG/EU_Orsieres.dwg', visible: true, label: 'desktop_download_orsieres_eu_dwg', zIndex: 13});
        this.downloadButtons.push({id:"downloadorsieres", label:"desktop_download", links:orsieres});
      }

      // A9 specific button
      if(this.instanceName == "a9" && this.gmfUser.username !== null) {
          var a9 = [];
          a9.push({id:'a9_metadaten', link: 'https://docs.vsgis.ch/a9/metadaten.pdf', visible: true, label: 'desktop_download_a9_metadaten', zIndex: 1});
          this.downloadButtons.push({id:"downloada9", label:"desktop_download", links:a9});
      }

      // GCSPDS specific button
      if(this.instanceName == "gcspds" && this.gmfUser.username !== null) {
        var anniviers = [];
        anniviers.push({id:'rep_anniviers', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=2feddb59cd5e089419a0f75f383f1d83&instance=gcspds', visible: true, label: 'Piquet Anniviers', zIndex: 11});
        anniviers.push({id:'inter_anniviers', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=e414e664a5c87750820ff7217fa46555&instance=gcspds', visible: true, label: 'Interventions Anniviers', zIndex: 11});
        anniviers.push({id:'rep_defi_anniviers', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=640eda5d4e1d68714e36f18dc887e8d0&instance=gcspds', visible: true, label: 'Défibrillateurs', zIndex: 11});
        anniviers.push({id:'rep_heb_urg_anniviers', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=a76fd90079975aa7a34e0b7d7db856cb&instance=gcspds', visible: true, label: 'Hébergement urgence', zIndex: 11});
        anniviers.push({id:'rep_nac_anniviers', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=35a600b9e2c802f90b54bcf158d7a594&instance=gcspds', visible: true, label: 'NAC', zIndex: 11});
        anniviers.push({id:'rep_partner_anniviers', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=044b1903cb3bdf453150e41d539ae394&instance=gcspds', visible: true, label: 'Partenaires', zIndex: 11});
        anniviers.push({id:'rep_place_rass_anniviers', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=5dc706ca152ca68660c0d38cc202a2f3&instance=gcspds', visible: true, label: 'Places de rassemblement', zIndex: 11});
        anniviers.push({id:'rep_plan_inter_anniviers', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=b5f5f1734d4647a01061eea7c17dcba5&instance=gcspds', visible: true, label: 'Plan intervention', zIndex: 11});
        anniviers.push({id:'rep_access_pipe_anniviers', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=da8dd7c39383db63942b5715768c9734&instance=gcspds', visible: true, label: 'Tubes accès', zIndex: 11});
        anniviers.push({id:'rep_interd_feu_anniviers', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=a7a2ae9471cf677a678f65cfee0e3aad&instance=gcspds', visible: true, label: 'Interdiction de faire du feu', zIndex: 11});
        anniviers.push({id:'rep_zone_patrouille_anniviers', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=ca52da35ebe3d752f5e211142c680103&instance=gcspds', visible: true, label: 'Zones de patrouille', zIndex: 11});
        anniviers.push({id:'rep_user_list', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=7d7fdd71f93857b5eb7b9ceaab6aa22e&instance=gcspds', visible: true, label: 'Liste des utilisateurs (xls)', zIndex: 11});

        this.downloadButtons.push({id:"anniviers", label:"Anniviers", links:anniviers});

        var cransmontana = [];
        cransmontana.push({id:'rep_cransmontana', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=4d88c3fa28d2b3f03702b76e1d29636c&instance=gcspds ', visible: true, label: 'Piquet Crans-Montana', zIndex: 11});
        cransmontana.push({id:'inter_cransmontana', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=37bf7c5dc92c986b1aa59ef275d35565&instance=gcspds ', visible: true, label: 'Interventions Crans-Montana', zIndex: 11});
        cransmontana.push({id:'rep_defi_cransmontana', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=c991382bd975b572f77082ad4af1ea42&instance=gcspds', visible: true, label: 'Défibrillateurs', zIndex: 11});
        cransmontana.push({id:'rep_heb_urg_cransmontana', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=b2b6c9a4008a42dea0b45a83f26a053a&instance=gcspds', visible: true, label: 'Hébergement urgence', zIndex: 11});
        cransmontana.push({id:'rep_nac_cransmontana', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=7306c65aca2edd20b457028a120d3501&instance=gcspds', visible: true, label: 'NAC', zIndex: 11});
        cransmontana.push({id:'rep_partner_cransmontana', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=52c34c41f744f4a697469786769b4f74&instance=gcspds', visible: true, label: 'Partenaires', zIndex: 11});
        cransmontana.push({id:'rep_place_rass_cransmontana', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=c7a6f1afcc3577cbba4c7ad7c23016b5&instance=gcspds', visible: true, label: 'Places de rassemblement', zIndex: 11});
        cransmontana.push({id:'rep_plan_inter_cransmontana', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=cfaddfdd26aa4f87916a3f1d534b1e24&instance=gcspds', visible: true, label: 'Plan intervention', zIndex: 11});
        cransmontana.push({id:'rep_access_pipe_cransmontana', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=62716c5d766b5c4c8a3e514de8b53bd5&instance=gcspds', visible: true, label: 'Tubes accès', zIndex: 11});
        cransmontana.push({id:'rep_interd_feu_cransmontana', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=dbff403c2aa4297297601a336595799a&instance=gcspds', visible: true, label: 'Interdiction de faire du feu', zIndex: 11});
        cransmontana.push({id:'rep_zone_patrouille_cransmontana', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=b39b719e9feda4fc9e913ca9e71b9bfe&instance=gcspds', visible: true, label: 'Zones de patrouille', zIndex: 11});
        cransmontana.push({id:'rep_user_list', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=7d7fdd71f93857b5eb7b9ceaab6aa22e&instance=gcspds', visible: true, label: 'Liste des utilisateurs (xls)', zIndex: 11});

        this.downloadButtons.push({id:"cransmontana", label:"Crans-Montana", links:cransmontana});

        var sierre = [];
        sierre.push({id:'rep_sierre', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=159e903a29391301533c3aedd6de6746&instance=gcspds ', visible: true, label: 'Piquet Sierre', zIndex: 11});
        sierre.push({id:'inter_sierre', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=39a1165d4e7685ef4e437cecf0584c0f&instance=gcspds ', visible: true, label: 'Interventions Sierre', zIndex: 11});
        sierre.push({id:'rep_defi_sierre', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=63baef3ae6d776b9a344ed010250a0ca&instance=gcspds', visible: true, label: 'Défibrillateurs', zIndex: 11});
        sierre.push({id:'rep_heb_urg_sierre', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=980cf6841713baee0d8c3df954c4f543&instance=gcspds', visible: true, label: 'Hébergement urgence', zIndex: 11});
        sierre.push({id:'rep_nac_sierre', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=1c8cc4fa012bc8b9451070e1abb60304&instance=gcspds', visible: true, label: 'NAC', zIndex: 11});
        sierre.push({id:'rep_partner_sierre', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=a8670749dd4e9a9f0ae893eaf98b8e8e&instance=gcspds', visible: true, label: 'Partenaires', zIndex: 11});
        sierre.push({id:'rep_place_rass_sierre', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=b3cc895fb14b7f76da84218651f086e9&instance=gcspds', visible: true, label: 'Places de rassemblement', zIndex: 11});
        sierre.push({id:'rep_plan_inter_sierre', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=318904454926e30e9ec897698ce936ed&instance=gcspds', visible: true, label: 'Plan intervention', zIndex: 11});
        sierre.push({id:'rep_access_pipe_sierre', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=1366677c4fd5f5e854e46b76ab33e185&instance=gcspds', visible: true, label: 'Tubes accès', zIndex: 11});
        sierre.push({id:'rep_interd_feu_sierre', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=0d4f6551d33aeaede37524461a48e135&instance=gcspds', visible: true, label: 'Interdiction de faire du feu', zIndex: 11});
        sierre.push({id:'rep_zone_patrouille_sierre', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=2822dbbdef7248df9736a54d28e6a28c&instance=gcspds', visible: true, label: 'Zones de patrouille', zIndex: 11});
        sierre.push({id:'rep_user_list', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=7d7fdd71f93857b5eb7b9ceaab6aa22e&instance=gcspds', visible: true, label: 'Liste des utilisateurs (xls)', zIndex: 11});

        this.downloadButtons.push({id:"sierre", label:"Sierre", links:sierre});

        var nobleContree = [];
        nobleContree.push({id:'rep_noblecontree', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=f6501bad86c51f9d6a3174d337265646&instance=gcspds ', visible: true, label: 'Piquet Noble-Contrée', zIndex: 11});
        nobleContree.push({id:'inter_noblecontree', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=a27ed5235e81dfefebb5c58b2576072c&instance=gcspds ', visible: true, label: 'Interventions Noble-Contrée', zIndex: 11});
        nobleContree.push({id:'rep_defi_noblecontree', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=5bb011dd03030ed288934342a7295931&instance=gcspds', visible: true, label: 'Défibrillateurs', zIndex: 11});
        nobleContree.push({id:'rep_heb_urg_noblecontree', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=ea37b6720d5c4d1de4cab1dc1a80e978&instance=gcspds', visible: true, label: 'Hébergement urgence', zIndex: 11});
        nobleContree.push({id:'rep_nac_noblecontree', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=81491cce4259bb0cb1e1e8a01048e3da&instance=gcspds', visible: true, label: 'NAC', zIndex: 11});
        nobleContree.push({id:'rep_partner_noblecontree', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=cb94d50e5e455e9ce01a281ea60925d2&instance=gcspds', visible: true, label: 'Partenaires', zIndex: 11});
        nobleContree.push({id:'rep_place_rass_noblecontree', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=fafb751c5d5c19cbc14a37fe90818cb2&instance=gcspds', visible: true, label: 'Places de rassemblement', zIndex: 11});
        nobleContree.push({id:'rep_plan_inter_noblecontree', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=9be86846f63c52dcb6e17152eb523396&instance=gcspds', visible: true, label: 'Plan intervention', zIndex: 11});
        nobleContree.push({id:'rep_access_pipe_noblecontree', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=2758c47d2da547512fec6cae571342d5&instance=gcspds', visible: true, label: 'Tubes accès', zIndex: 11});
        nobleContree.push({id:'rep_interd_feu_noblecontree', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=55f1c48c5783b762e6686b97d86258e4&instance=gcspds', visible: true, label: 'Interdiction de faire du feu', zIndex: 11});
        nobleContree.push({id:'rep_zone_patrouille_noblecontree', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=7dd0b9aff74eb24e813784323afb02df&instance=gcspds', visible: true, label: 'Zones de patrouille', zIndex: 11});
        nobleContree.push({id:'rep_user_list', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=7d7fdd71f93857b5eb7b9ceaab6aa22e&instance=gcspds', visible: true, label: 'Liste des utilisateurs (xls)', zIndex: 11});

        this.downloadButtons.push({id:"noblecontree", label:"Noble-Contrée", links:nobleContree});
        
        var salgesch = [];
        salgesch.push({id:'rep_salgesch', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=c85f2e630dc8991c54ac9e69efd2c8f2&instance=gcspds ', visible: true, label: 'Pikett Salgesch Feuerwehr', zIndex: 11});
        salgesch.push({id:'inter_salgesch', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=1aca07056e3cd57cc772c775e4d852e0&instance=gcspds ', visible: true, label: 'Interventionen Salgesch Feuerwehr', zIndex: 11});
        salgesch.push({id:'rep_defi_salgesch', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=0ecf6b5e7e502e436b077c335b4aaf71&instance=gcspds', visible: true, label: 'Défibrillateurs', zIndex: 11});
        salgesch.push({id:'rep_heb_urg_salgesch', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=f3e1452de0517a0fdb89bc6511ed3579&instance=gcspds', visible: true, label: 'Hébergement urgence', zIndex: 11});
        salgesch.push({id:'rep_nac_salgesch', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=d94f58bb52d76cd7fa1eea137a059e62&instance=gcspds', visible: true, label: 'NAC', zIndex: 11});
        salgesch.push({id:'rep_partner_salgesch', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=e382961d76cb8fbad4e45436c2bcbae9&instance=gcspds', visible: true, label: 'Partenaires', zIndex: 11});
        salgesch.push({id:'rep_place_rass_salgesch', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=9a15f4d5d225e07ae33c5bdd31f9853d&instance=gcspds', visible: true, label: 'Places de rassemblement', zIndex: 11});
        salgesch.push({id:'rep_plan_inter_salgesch', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=3ae5f020eecc3d88c59c9be20876afff&instance=gcspds', visible: true, label: 'Plan intervention', zIndex: 11});
        salgesch.push({id:'rep_access_pipe_salgesch', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=0ea80a31eea21464dccdafd5a86b99ab&instance=gcspds', visible: true, label: 'Tubes accès', zIndex: 11});
        salgesch.push({id:'rep_interd_feu_salgesch', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=ca3c73eea22d2c1762b1eda085ae016f&instance=gcspds', visible: true, label: 'Interdiction de faire du feu', zIndex: 11});
        salgesch.push({id:'rep_zone_patrouille_salgesch', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=414a1a3e38fde367b70964a37083405c&instance=gcspds', visible: true, label: 'Zones de patrouille', zIndex: 11});
        salgesch.push({id:'rep_user_list', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=7d7fdd71f93857b5eb7b9ceaab6aa22e&instance=gcspds', visible: true, label: 'Liste des utilisateurs (xls)', zIndex: 11});

        this.downloadButtons.push({id:"salgesch", label:"Salgesch Feuerwehr", links:salgesch});
        
        var vallon = [];
        vallon.push({id:'rep_vallon', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=c1f22e00af7c1f79632b4480f9db885b&instance=gcspds ', visible: true, label: 'Piquet CSP du Vallon', zIndex: 11});
        vallon.push({id:'inter_vallon', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=0b1e6e6d0916b5c38a0c27b9dbab8e25&instance=gcspds ', visible: true, label: 'Interventionen CSP du Vallon', zIndex: 11});
        vallon.push({id:'rep_defi_vallon', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=be4e72aaba7a4e657a8901e81758b26d&instance=gcspds', visible: true, label: 'Défibrillateurs', zIndex: 11});
        vallon.push({id:'rep_heb_urg_vallon', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=157cb2777d6af3602baa1272dd43ac99&instance=gcspds', visible: true, label: 'Hébergement urgence', zIndex: 11});
        vallon.push({id:'rep_nac_vallon', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=b1c2ac4487ba153b8a2327a1e1601919&instance=gcspds', visible: true, label: 'NAC', zIndex: 11});
        vallon.push({id:'rep_partner_vallon', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=bfb1f9ba90bd40f13201bde15ba5a958&instance=gcspds', visible: true, label: 'Partenaires', zIndex: 11});
        vallon.push({id:'rep_place_rass_vallon', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=c72de60bfe0880885c48282aa7f4450e&instance=gcspds', visible: true, label: 'Places de rassemblement', zIndex: 11});
        vallon.push({id:'rep_plan_inter_vallon', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=02399fc2d8efad91a4b22c8cc86c8e33&instance=gcspds', visible: true, label: 'Plan intervention', zIndex: 11});
        vallon.push({id:'rep_access_pipe_vallon', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=355acbee07e83df30c7e3e46860f10b5&instance=gcspds', visible: true, label: 'Tubes accès', zIndex: 11});
        vallon.push({id:'rep_interd_feu_vallon', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=f3d56156c0879fe43b84cd046a85d637&instance=gcspds', visible: true, label: 'Interdiction de faire du feu', zIndex: 11});
        vallon.push({id:'rep_zone_patrouille_vallon', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=00a7ceddd0b477834a918d145c687e5c&instance=gcspds', visible: true, label: 'Zones de patrouille', zIndex: 11});
        vallon.push({id:'rep_user_list', link: 'https://map.vsgis.ch/pompiers/getfile.php?target=7d7fdd71f93857b5eb7b9ceaab6aa22e&instance=gcspds', visible: true, label: 'Liste des utilisateurs (xls)', zIndex: 11});

        this.downloadButtons.push({id:"vallon", label:"CSP du Vallon", links:vallon});
      }
      // CSP Salentin specific button
      if(this.instanceName == "cspsalentin" && this.gmfUser.username !== null) {
        var cspSalentin = [];
        cspSalentin.push({id:'rep_salentin', link: 'https://docs.vsgis.ch/cspsalentin/03c90af7fe896c276b2d96f6b584e39a.html', visible: true, label: 'Piquet Salentin', zIndex: 11});
        cspSalentin.push({id:'inter_salentin', link: 'https://docs.vsgis.ch/cspsalentin/bc30dbedf79a740f04014a7f33daadd1.html', visible: true, label: 'Interventions Salentin', zIndex: 11});
        cspSalentin.push({id:'rep_defi_salentin', link: 'https://docs.vsgis.ch/cspsalentin/2f4fba47dc8949ca42e0582dffcb923a.html', visible: true, label: 'Défibrillateurs', zIndex: 11});
        cspSalentin.push({id:'rep_heb_urg_salentin', link: 'https://docs.vsgis.ch/cspsalentin/0f5a6c9346ae3d0112c0672c54381a6c.html', visible: true, label: 'Hébergement urgence', zIndex: 11});
        cspSalentin.push({id:'rep_nac_salentin', link: 'https://docs.vsgis.ch/cspsalentin/ce1373e3bfb42763c076d7d550f5dbfd.html', visible: true, label: 'NAC', zIndex: 11});
        cspSalentin.push({id:'rep_partner_salentin', link: 'https://docs.vsgis.ch/cspsalentin/47484dd6c8e2142f715be83d66dabca8.html', visible: true, label: 'Partenaires', zIndex: 11});
        cspSalentin.push({id:'rep_place_rass_salentin', link: 'https://docs.vsgis.ch/cspsalentin/66fdb2decdd088ca3d3d81c98719ef94.html', visible: true, label: 'Places de rassemblement', zIndex: 11});
        cspSalentin.push({id:'rep_plan_inter_salentin', link: 'https://docs.vsgis.ch/cspsalentin/ad82445587007b53ed69ceea2608cc43.html', visible: true, label: 'Plan intervention', zIndex: 11});
        cspSalentin.push({id:'rep_access_pipe_salentin', link: 'https://docs.vsgis.ch/cspsalentin/dfffdbc4aea088d3b8a883428dcedcca.html', visible: true, label: 'Tubes accès', zIndex: 11});
        cspSalentin.push({id:'rep_user_list', link: 'https://docs.vsgis.ch/cspsalentin/cae3f33070b8dc207f43e6fb8c078613.xlsx', visible: true, label: 'Liste des utilisateurs (xls)', zIndex: 11});
        this.downloadButtons.push({id:"cspsalentin", label:"CSP Salentin", links:cspSalentin});
      }
      // CSP Saviese specific button
      if(this.instanceName == "cspsaviese" && this.gmfUser.username !== null) {
        var cspSaviese = [];
        cspSaviese.push({id:'rep_saviese', link: 'https://docs.vsgis.ch/cspsaviese/a3ae5c3a26cd17b7cf2effc54778e6ef.html', visible: true, label: 'Piquet Savièse', zIndex: 11});
        cspSaviese.push({id:'inter_saviese', link: 'https://docs.vsgis.ch/cspsaviese/5648d8a930de1235e10c585311b85573.html', visible: true, label: 'Interventions Savièse', zIndex: 11});
        cspSaviese.push({id:'rep_defi_saviese', link: 'https://docs.vsgis.ch/cspsaviese/44ae63a7fe1f56ee5da451e624bc5e40.html', visible: true, label: 'Défibrillateurs', zIndex: 11});
        cspSaviese.push({id:'rep_heb_urg_saviese', link: 'https://docs.vsgis.ch/cspsaviese/6a2193202084fd665edc6812f20421d0.html', visible: true, label: 'Hébergement urgence', zIndex: 11});
        cspSaviese.push({id:'rep_nac_saviese', link: 'https://docs.vsgis.ch/cspsaviese/5312c3abd87150547aa383e985dcfe65.html', visible: true, label: 'NAC', zIndex: 11});
        cspSaviese.push({id:'rep_partner_saviese', link: 'https://docs.vsgis.ch/cspsaviese/26297f5750aa1b2aa46279e9d0723c2e.html', visible: true, label: 'Partenaires', zIndex: 11});
        cspSaviese.push({id:'rep_place_rass_saviese', link: 'https://docs.vsgis.ch/cspsaviese/a47f757cd20359666c7fcf4363812278.html', visible: true, label: 'Places de rassemblement', zIndex: 11});
        cspSaviese.push({id:'rep_plan_inter_saviese', link: 'https://docs.vsgis.ch/cspsaviese/23171164e869bd6275b0a0e963954c4f.html', visible: true, label: 'Plan intervention', zIndex: 11});
        cspSaviese.push({id:'rep_access_pipe_saviese', link: 'https://docs.vsgis.ch/cspsaviese/0182ab770b0803d84bdce6c3514a5343.html', visible: true, label: 'Tubes accès', zIndex: 11});
        cspSaviese.push({id:'rep_interd_feu_saviese', link: 'https://docs.vsgis.ch/cspsaviese/91b3855ffeea0edc43449b5e67487d1c.html', visible: true, label: 'Interdiction de faire du feu', zIndex: 11});
        cspSaviese.push({id:'rep_zone_patrouille_saviese', link: 'https://docs.vsgis.ch/cspsaviese/c1d5bb5d07c4e4acf12c11c5d212cfd6.html', visible: true, label: 'Zones de patrouille', zIndex: 11});
        cspSaviese.push({id:'rep_user_list', link: 'https://docs.vsgis.ch/cspsaviese/47ad4c0623e69752ee354e494181e32f.xlsx', visible: true, label: 'Liste des utilisateurs (xls)', zIndex: 11});
        this.downloadButtons.push({id:"cspSaviese", label:"CSP Savièse", links:cspSaviese});
      }
	  
	  // CSP Sion specific button
      if(this.instanceName == "csision" && this.gmfUser.username !== null) {
        var csiSion = [];
        csiSion.push({id:'rep_sion', link: 'https://docs.vsgis.ch/csision/ed85f343a7b0ed242fb10dad4c1959d8.html', visible: true, label: 'Piquet Sion', zIndex: 11});
        csiSion.push({id:'inter_sion', link: 'https://docs.vsgis.ch/csision/3dd5c45cd21191c1b25bef339f105aa9.html', visible: true, label: 'Interventions Sion', zIndex: 11});
        csiSion.push({id:'rep_defi_sion', link: 'https://docs.vsgis.ch/csision/837db735cfe5abc06fc2cac76dfe9e09.html', visible: true, label: 'Défibrillateurs', zIndex: 11});
        csiSion.push({id:'rep_heb_urg_sion', link: 'https://docs.vsgis.ch/csision/2842204da905939b53a4f972ab34c206.html', visible: true, label: 'Hébergement urgence', zIndex: 11});
        csiSion.push({id:'rep_nac_sion', link: 'https://docs.vsgis.ch/csision/91d2f185c7eaa4c98c189bdcbbf3de3b.html', visible: true, label: 'NAC', zIndex: 11});
        csiSion.push({id:'rep_partner_sion', link: 'https://docs.vsgis.ch/csision/094c1721c5280ea98bcf68f14c7a0a7e.html', visible: true, label: 'Partenaires', zIndex: 11});
        csiSion.push({id:'rep_place_rass_sion', link: 'https://docs.vsgis.ch/csision/62d02578f74b16d0192d7bf575f1734b.html', visible: true, label: 'Places de rassemblement', zIndex: 11});
        csiSion.push({id:'rep_plan_inter_sion', link: 'https://docs.vsgis.ch/csision/d6c6addd8c18ed785e5277480cd07198.html', visible: true, label: 'Plan intervention', zIndex: 11});
        csiSion.push({id:'rep_access_pipe_sion', link: 'https://docs.vsgis.ch/csision/b797d122cf6d00394dcc60e4bdf8cc05.html', visible: true, label: 'Tubes accès', zIndex: 11});
        //csiSion.push({id:'rep_interd_feu_sion', link: 'https://docs.vsgis.ch/csision/91f8cd29c74ed5f8e27b916f85b66234.html', visible: true, label: 'Interdiction de faire du feu', zIndex: 11});
        //csiSion.push({id:'rep_zone_patrouille_sion', link: 'https://docs.vsgis.ch/csision/3a27d26d9dc8c2d29513cee0d136f8cf.html', visible: true, label: 'Zones de patrouille', zIndex: 11});
        csiSion.push({id:'rep_user_list', link: 'https://docs.vsgis.ch/csision/3618ad86783bd03ededd1145cf66e9a6.xlsx', visible: true, label: 'Liste des utilisateurs (xls)', zIndex: 11});
        this.downloadButtons.push({id:"csiSion", label:"CSI Sion", links:csiSion});
      }
	  
      // CSI Printse specific button
      if(this.instanceName == "csiprintse" && this.gmfUser.username !== null) {
        var csiPrintse = [];
        csiPrintse.push({id:'rep_printse', link: 'https://docs.vsgis.ch/csiprintse/3be51f0a3fc48cb9024e435ecf330b21.html', visible: true, label: 'Piquet Printse', zIndex: 11});
        csiPrintse.push({id:'inter_printse', link: 'https://docs.vsgis.ch/csiprintse/5725c7ae70e302c52ec72fdf182f806e.html', visible: true, label: 'Interventions Printse', zIndex: 11});
        csiPrintse.push({id:'rep_defi_printse', link: 'https://docs.vsgis.ch/csiprintse/c847d20352215a1cc147d2141171feb9.html', visible: true, label: 'Défibrillateurs', zIndex: 11});
        csiPrintse.push({id:'rep_heb_urg_printse', link: 'https://docs.vsgis.ch/csiprintse/c8ef4ac4818c1baeaa052982b6a42877.html', visible: true, label: 'Hébergement urgence', zIndex: 11});
        csiPrintse.push({id:'rep_nac_printse', link: 'https://docs.vsgis.ch/csiprintse/cc479501b48c78db000b6205642ef4e9.html', visible: true, label: 'NAC', zIndex: 11});
        csiPrintse.push({id:'rep_partner_printse', link: 'https://docs.vsgis.ch/csiprintse/b583ad28710a8cfa8c6be95cb69a2f98.html', visible: true, label: 'Partenaires', zIndex: 11});
        csiPrintse.push({id:'rep_place_rass_printse', link: 'https://docs.vsgis.ch/csiprintse/fe9e898e287afaf6183c9237571745f8.html', visible: true, label: 'Places de rassemblement', zIndex: 11});
        csiPrintse.push({id:'rep_plan_inter_printse', link: 'https://docs.vsgis.ch/csiprintse/73ba6d72dbc014ef0c1035ddf8169f61.html', visible: true, label: 'Plan intervention', zIndex: 11});
        csiPrintse.push({id:'rep_access_pipe_printse', link: 'https://docs.vsgis.ch/csiprintse/d70d81734ef7586a443e5ff45d0b5df9.html', visible: true, label: 'Tubes accès', zIndex: 11});
        csiPrintse.push({id:'rep_user_list', link: 'https://docs.vsgis.ch/csiprintse/f43b53c42708e63b0d8d2b7d8ce8264b.xlsx', visible: true, label: 'Liste des utilisateurs (xls)', zIndex: 11});
        this.downloadButtons.push({id:"csiprintse", label:"CSI Printse", links:csiPrintse});
      }
      // CSI Martigny specific button
      if(this.instanceName == "csimartigny" && this.gmfUser.username !== null) {
        var csiMartigny = [];
        csiMartigny.push({id:'rep_csimartigny', link: 'https://docs.vsgis.ch/csimartigny/f0946bc1537e2397b9cb3395d2a9a1f7.html', visible: true, label: 'Piquet CSI Martigny', zIndex: 11});
        csiMartigny.push({id:'inter_csimartigny', link: 'https://docs.vsgis.ch/csimartigny/63758218736dfaf43d9214a169177298.html', visible: true, label: 'Interventions CSI Martigny', zIndex: 11});
        csiMartigny.push({id:'rep_defi_csimartigny', link: 'https://docs.vsgis.ch/csimartigny/3bed5972c0520d7a220e904d50c5d9bd.html', visible: true, label: 'Défibrillateurs', zIndex: 11});
        csiMartigny.push({id:'rep_heb_urg_csimartigny', link: 'https://docs.vsgis.ch/csimartigny/dc01dfca7b613b23ec72457c19dd290d.html', visible: true, label: 'Hébergement urgence', zIndex: 11});
        csiMartigny.push({id:'rep_nac_csimartigny', link: 'https://docs.vsgis.ch/csimartigny/e7e1ce8e14a3ae3f0f56dfdede014a2b.html', visible: true, label: 'NAC', zIndex: 11});
        csiMartigny.push({id:'rep_partner_csimartigny', link: 'https://docs.vsgis.ch/csimartigny/1ae67d074342bed43e4048ad2cc96b59.html', visible: true, label: 'Partenaires', zIndex: 11});
        csiMartigny.push({id:'rep_place_rass_csimartigny', link: 'https://docs.vsgis.ch/csimartigny/e70a8e3d4701d57fe0bcfba397a176f1.html', visible: true, label: 'Places de rassemblement', zIndex: 11});
        csiMartigny.push({id:'rep_plan_inter_csimartigny', link: 'https://docs.vsgis.ch/csimartigny/cb8a0140cce61b003b868bc73556e424.html', visible: true, label: 'Plan intervention', zIndex: 11});
        csiMartigny.push({id:'rep_access_pipe_csimartigny', link: 'https://docs.vsgis.ch/csimartigny/ac8b099bd676bd6844a69caa774712b2.html', visible: true, label: 'Tubes accès', zIndex: 11});
        csiMartigny.push({id:'rep_user_list', link: 'https://docs.vsgis.ch/csimartigny/087cc88f1a7296695b12bc74979f4ed1.xlsx', visible: true, label: 'Liste des utilisateurs (xls)', zIndex: 11});
        this.downloadButtons.push({id:"csimartigny", label:"Martigny et regions", links:csiMartigny});
      }
      // SCVS specific button
      if(this.instanceName == "scvs" && this.gmfUser.username !== null) {
        var chablaisVs = [];
        chablaisVs.push({id:'rep_chablais_vs', link: 'https://docs.vsgis.ch/scvs/a51097bf53e4d832b246019a039fc290.html', visible: true, label: 'Piquet', zIndex: 11});
        chablaisVs.push({id:'inter_chablais_vs', link: 'https://docs.vsgis.ch/scvs/858af21a2f296710971effbe8bf78d7e.html', visible: true, label: 'Interventions', zIndex: 11});
        chablaisVs.push({id:'rep_defi_chablais_vs', link: 'https://docs.vsgis.ch/scvs/0c591cffa119cae8a092fd1a1b65ef92.html', visible: true, label: 'Défibrillateurs', zIndex: 11});
        chablaisVs.push({id:'rep_heb_urg_chablais_vs', link: 'https://docs.vsgis.ch/scvs/1510455d15248079d77055a2f165a999.html', visible: true, label: 'Hébergement urgence', zIndex: 11});
        chablaisVs.push({id:'rep_nac_chablais_vs', link: 'https://docs.vsgis.ch/scvs/5d962c1f6702275241a49feb60c8b3a0.html', visible: true, label: 'NAC', zIndex: 11});
        chablaisVs.push({id:'rep_partner_chablais_vs', link: 'https://docs.vsgis.ch/scvs/d0ccbe8cdee8649f6d59dd50ad10e60f.html', visible: true, label: 'Partenaires', zIndex: 11});
        chablaisVs.push({id:'rep_place_rass_chablais_vs', link: 'https://docs.vsgis.ch/scvs/100e9344a5c9dac846cee32abc9b8625.html', visible: true, label: 'Places de rassemblement', zIndex: 11});
        chablaisVs.push({id:'rep_plan_inter_chablais_vs', link: 'https://docs.vsgis.ch/scvs/334a245b2c0a9c19cbcdbd17c20e40ca.html', visible: true, label: 'Plan intervention', zIndex: 11});
        chablaisVs.push({id:'rep_access_pipe_chablais_vs', link: 'https://docs.vsgis.ch/scvs/8cc59a1d58cd39c894b113c152346191.html', visible: true, label: 'Tubes accès', zIndex: 11});
        chablaisVs.push({id:'rep_user_list', link: 'https://docs.vsgis.ch/scvs/e37b931342f23aca6064062cc3aa02d1.xlsx', visible: true, label: 'Liste des utilisateurs (xls)', zIndex: 11});
        this.downloadButtons.push({id:"chablais_vs", label:"Chablais VS", links:chablaisVs});

        var cimo = [];
        cimo.push({id:'rep_cimo', link: 'https://docs.vsgis.ch/scvs/105d040f8801197cf3721bf1cdcbc26f.html', visible: true, label: 'Piquet', zIndex: 11});
        cimo.push({id:'inter_cimo', link: 'https://docs.vsgis.ch/scvs/16f23f9162e21d3e8aed6d65854739c0.html', visible: true, label: 'Interventions', zIndex: 11});
        cimo.push({id:'rep_defi_cimo', link: 'https://docs.vsgis.ch/scvs/1dfbad05323ad26ea141089aa2af2b3e.html', visible: true, label: 'Défibrillateurs', zIndex: 11});
        cimo.push({id:'rep_heb_urg_cimo', link: 'https://docs.vsgis.ch/scvs/db1991812209c052a929812b1350f091.html', visible: true, label: 'Hébergement urgence', zIndex: 11});
        cimo.push({id:'rep_nac_cimo', link: 'https://docs.vsgis.ch/scvs/b375e8d57acf0cefef6a082bf8fc736d.html', visible: true, label: 'NAC', zIndex: 11});
        cimo.push({id:'rep_partner_cimo', link: 'https://docs.vsgis.ch/scvs/304293debf97b558ede6972bdc76a9d1.html', visible: true, label: 'Partenaires', zIndex: 11});
        cimo.push({id:'rep_place_rass_cimo', link: 'https://docs.vsgis.ch/scvs/217697d170bddf2289f21e7e36993ae4.html', visible: true, label: 'Places de rassemblement', zIndex: 11});
        cimo.push({id:'rep_plan_inter_cimo', link: 'https://docs.vsgis.ch/scvs/29b50b8209fb6ca00c2e1a38b39c2fd1.html', visible: true, label: 'Plan intervention', zIndex: 11});
        cimo.push({id:'rep_access_pipe_cimo', link: 'https://docs.vsgis.ch/scvs/975259310e0f7d932e3f4b99520629e7.html', visible: true, label: 'Tubes accès', zIndex: 11});
        cimo.push({id:'rep_user_list', link: 'https://docs.vsgis.ch/scvs/e37b931342f23aca6064062cc3aa02d1.xlsx', visible: true, label: 'Liste des utilisateurs (xls)', zIndex: 11});
        this.downloadButtons.push({id:"cimo", label:"CIMO", links:cimo});

        var hautLac = [];
        hautLac.push({id:'rep_haut_lac', link: 'https://docs.vsgis.ch/scvs/7769b3b2f289ac3f59248fe075ceead0.html', visible: true, label: 'Piquet', zIndex: 11});
        hautLac.push({id:'inter_haut_lac', link: 'https://docs.vsgis.ch/scvs/4ba5180ca12693eee2f55ffae921a4b6.html', visible: true, label: 'Interventions', zIndex: 11});
        hautLac.push({id:'rep_defi_haut_lac', link: 'https://docs.vsgis.ch/scvs/56ee3f16ac0ee3d65cede47441d47d44.html', visible: true, label: 'Défibrillateurs', zIndex: 11});
        hautLac.push({id:'rep_heb_urg_haut_lac', link: 'https://docs.vsgis.ch/scvs/c761b3fe053410fedf3235fbcc78f1d4.html', visible: true, label: 'Hébergement urgence', zIndex: 11});
        hautLac.push({id:'rep_nac_haut_lac', link: 'https://docs.vsgis.ch/scvs/2d0029ed89a0668ab5e354d3f57c8c5f.html', visible: true, label: 'NAC', zIndex: 11});
        hautLac.push({id:'rep_partner_haut_lac', link: 'https://docs.vsgis.ch/scvs/49cd3136bb87a6ad2e486dcb0a7b474e.html', visible: true, label: 'Partenaires', zIndex: 11});
        hautLac.push({id:'rep_place_rass_haut_lac', link: 'https://docs.vsgis.ch/scvs/c580ba2b9176de6e63a7b6169214eeb6.html', visible: true, label: 'Places de rassemblement', zIndex: 11});
        hautLac.push({id:'rep_plan_inter_haut_lac', link: 'https://docs.vsgis.ch/scvs/1c445c717080be0f6caa127f1490f9b1.html', visible: true, label: 'Plan intervention', zIndex: 11});
        hautLac.push({id:'rep_access_pipe_haut_lac', link: 'https://docs.vsgis.ch/scvs/7ada75d09361c768e606fd2da8743278.html', visible: true, label: 'Tubes accès', zIndex: 11});
        hautLac.push({id:'rep_user_list', link: 'https://docs.vsgis.ch/scvs/e37b931342f23aca6064062cc3aa02d1.xlsx', visible: true, label: 'Liste des utilisateurs (xls)', zIndex: 11});
        this.downloadButtons.push({id:"haut_lac", label:"Haut-Lac", links:hautLac});

        var dentMidi = [];
        dentMidi.push({id:'rep_dent_midi', link: 'https://docs.vsgis.ch/scvs/2ee03e90e17aa6574a7c69b382b23fb8.html', visible: true, label: 'Piquet', zIndex: 11});
        dentMidi.push({id:'inter_dent_midi', link: 'https://docs.vsgis.ch/scvs/c9db4d4136027d330dbd4941b815633e.html', visible: true, label: 'Interventions', zIndex: 11});
        dentMidi.push({id:'rep_defi_dent_midi', link: 'https://docs.vsgis.ch/scvs/d1ee39b0454068a45436f1a8dfb74a72.html', visible: true, label: 'Défibrillateurs', zIndex: 11});
        dentMidi.push({id:'rep_heb_urg_dent_midi', link: 'https://docs.vsgis.ch/scvs/a53dad5c2339c35ee9bff6372fc5ec84.html', visible: true, label: 'Hébergement urgence', zIndex: 11});
        dentMidi.push({id:'rep_nac_dent_midi', link: 'https://docs.vsgis.ch/scvs/3c76c2071affb1223c5b9297784f8b4f.html', visible: true, label: 'NAC', zIndex: 11});
        dentMidi.push({id:'rep_partner_dent_midi', link: 'https://docs.vsgis.ch/scvs/f8ca5ab512e972926872501ad5d0e514.html', visible: true, label: 'Partenaires', zIndex: 11});
        dentMidi.push({id:'rep_place_rass_dent_midi', link: 'https://docs.vsgis.ch/scvs/9987bf4dd5b65d3ce7195624645cbd51.html', visible: true, label: 'Places de rassemblement', zIndex: 11});
        dentMidi.push({id:'rep_plan_inter_dent_midi', link: 'https://docs.vsgis.ch/scvs/be881f41262aad04be8093239d267d65.html', visible: true, label: 'Plan intervention', zIndex: 11});
        dentMidi.push({id:'rep_access_pipe_dent_midi', link: 'https://docs.vsgis.ch/scvs/93d2118d3d7452521112007d476d430e.html', visible: true, label: 'Tubes accès', zIndex: 11});
        dentMidi.push({id:'rep_user_list', link: 'https://docs.vsgis.ch/scvs/e37b931342f23aca6064062cc3aa02d1.xlsx', visible: true, label: 'Liste des utilisateurs (xls)', zIndex: 11});
        this.downloadButtons.push({id:"dent_midi", label:"Dents du Midi", links:dentMidi});
      }

      // CST/MO as DWG
      var downloadService = [];
      if(["anniviers", "cransmontana", "orsieres"].includes(this.instanceName) && this.gmfUser.username !== null ) {
        downloadService.push({id:'download_cst_dwg', link: 'https://www.vsgis.ch/'+this.lang+'/services', visible: true, label: 'desktop_download_cst_dwg', zIndex: 10});
        downloadService.push({id:'download_mo_dwg', link: 'https://www.vsgis.ch/'+this.lang+'/services', visible: true, label: 'desktop_download_mo_dwg', zIndex: 20});
        this.downloadButtons.push({id:"downloadservice", label:"desktop_download", links:downloadService});
      }
      if(["aluinfra"].includes(this.instanceName) && this.gmfUser.username !== null ) {
        downloadService.push({id:'download_cst_dwg', link: 'https://www.vsgis.ch/'+this.lang+'/services', visible: true, label: 'desktop_download_cst_dwg', zIndex: 10});
        this.downloadButtons.push({id:"downloadservice", label:"desktop_download", links:downloadService});
      }
      this.downloadButtonsAvailable = this.downloadButtons.length > 0 ? true  : false ;
    };
  }
}

/**
 * @hidden
 */
const module = angular.module('Appdesktop', [
  rudazModule.name,
  gmfControllersAbstractDesktopController.name,
  rudazInfra3DModule.name,
  rudazOrbis360Module.name,
  rudazFireSimulatorModule.name,
  gmfLidarprofileModule.name,
  ngeoGooglestreetviewModule.name,
  ngeoGeolocationDesktop.name,
  ngeoMessagePopup.name,
]);

module.value('gmfContextualdatacontentTemplateUrl', 'gmf/contextualdata');
module.value('pytreeLidarprofileJsonUrl', 'https://pytree.vsgis.ch');
module.run(/* @ngInject */ ($templateCache) => {
  // @ts-ignore: webpack
  $templateCache.put('gmf/contextualdata', require('./contextualdata.html'));
});

module.controller('DesktopController', Controller);

export default module;
